import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useMemo,
  useCallback,
} from "react";
import io from "socket.io-client";
import toast from "react-hot-toast";
import { useHistory } from "react-router-dom";
import moment from "moment";
import {
  Formik,
  Form,
  FieldArray,
  Field,
  ErrorMessage,
  useFormikContext,
} from "formik";
import * as Yup from "yup";
import { MdPreview } from "react-icons/md";
import { FiArrowRight } from "react-icons/fi";
import "../../styles/LiveSession.css";
import { AuthContext } from "../../context/AuthContext";
import LiveSessionServices from "../../services/LiveSessionServices";
import {
  getStoredActiveGameTab,
  setStoredActiveGameTab,
} from "../../utils/utils";
import SettingServices from "../../services/SettingServices";
import Loader from "../../components/Loader";
import useActiveGames from "../../hooks/useActiveGames";
import { getBoardGridStyle } from "../../utils/boardLayout";
import NoData from "../../components/NoData";
import GameSelect from "../../components/GameSelect";
import { GoDotFill } from "react-icons/go";
import { MdClose } from "react-icons/md";
import JackpotWinnerServices from "../../services/JackpotWinnerServices";
import JackpotXWinnerServices from "../../services/JackpotXWinnerServices";
import ManualWinnerServices from "../../services/ManualWinnerServices";
import DashboardServices from "../../services/DashboardServices";
import { LIVE_YANTRA_IMAGE_URL } from "../../utils/utils";

// Up to this many games fit as a row of tabs; past it a picker takes over.
const MAX_GAME_TABS = 3;

const validationSchema = Yup.object({
  wallet_balance: Yup.number().min(1, "Insufficient wallet balance."),
  total_bet: Yup.number().min(1, "Please place a bet."),

  yantra_list: Yup.array()
    .of(
      Yup.object({
        yantra_id: Yup.number().required(),
        yantra_count: Yup.number()
          .min(0, "Invalid count")
          .integer("Must be integer"),
      }),
    )
    .min(1, "At least one yantra required"),
});

/** Syncs total_bet and amount into form state when yantra counts change (useEffect to avoid infinite loop). */
const SyncTotalBetAmount = () => {
  const { values, setFieldValue } = useFormikContext();
  const list = values.yantra_list || [];
  const countsKey = JSON.stringify(
    list.map((y) => Number(y.yantra_count) || 0),
  );

  useEffect(() => {
    const totalBet = list.reduce(
      (sum, y) => sum + (Number(y.yantra_count) || 0),
      0,
    );
    const amount = list.reduce(
      (sum, y) =>
        sum + (Number(y.yantra_count) || 0) * (Number(y.yantra_amount) || 0),
      0,
    );
    setFieldValue("total_bet", totalBet);
    setFieldValue("amount", amount);
  }, [countsKey, setFieldValue]); // list not in deps: use countsKey so effect runs only when counts change

  return null;
};

/** Single or comma-separated winner yantra ids (e.g. JACKPOT "1,2,3"). */
function parseSessionWinnerYantraIds(raw) {
  if (raw == null || raw === "") return [];
  const s = String(raw).trim();
  if (!s || s === "0") return [];
  return s
    .split(",")
    .map((x) => Number(String(x).trim()))
    .filter((n) => Number.isFinite(n) && n > 0);
}

/** Same as server `parseWinnerX` / `getJackpotXWinPerUnit` (2x→200, 3x→300, 5x→500; legacy 20x→200). */
function parseJackpotXMultiplier(raw) {
  if (raw == null) return 0;
  const num = Number(String(raw).replace(/[^0-9.]/g, ""));
  return Number.isFinite(num) && num > 0 ? num : 0;
}

function getJackpotXWinPerUnitClient(winnerXRaw) {
  const x = Math.max(0, parseJackpotXMultiplier(winnerXRaw));
  if (!x) return 0;
  return x <= 10 ? x * 100 : x * 10;
}

/** Quick-pick labels for Jackpot-X popover (must match settlement rules). */
const JACKPOTX_QUICK_OPTIONS = ["2x", "3x", "4x", "5x"];

// Only used before a board has loaded. The real columns are the game's
// own yantras - a fixed 1..10 was wrong the moment a game had 12.
const DEFAULT_PURCHASE_GRID_YANTRA_IDS = [];

function emptySuperAdminPurchaseGrid(sessionDbId) {
  return {
    session_id: sessionDbId,
    yantra_ids: DEFAULT_PURCHASE_GRID_YANTRA_IDS,
    rows: [],
  };
}

/** Super Admin header: online users by role (same API as dashboard). */
const LS_ONLINE_POLL_MS = 25000;
const LS_ONLINE_MODAL_KEYS = {
  // admin: { title: "Online Admin", listKey: "admin" },
  outlet: { title: "Online Outlet", listKey: "outlet" },
  mobile: { title: "Online Mobile", listKey: "mobile" },
};

function formatLsOnlineUserLine(u) {
  const name = (u?.name || "").trim() || "—";
  const un = (u?.username || "").trim() || "—";
  return `${name} [${un}]`;
}

function normalizeJackpotXInput(s) {
  return String(s || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");
}

/**
 * JACKPOTX preview: pool from all yantras; payout = bet units on current session winner yantra(s) × win per unit.
 */
function computeJackpotXProfitPreview(
  yantraWiseList,
  winnerYantraRaw,
  winnerXInput,
  settlementSettings,
  sessionPercentage,
) {
  if (!Array.isArray(yantraWiseList) || yantraWiseList.length === 0)
    return null;
  const round2 = (n) => {
    const x = Number(n);
    if (!Number.isFinite(x)) return 0;
    return Math.round((x + Number.EPSILON) * 100) / 100;
  };
  const betUnit =
    Number(settlementSettings?.yantra_amount) > 0
      ? Number(settlementSettings.yantra_amount)
      : 11;
  const mult = parseJackpotXMultiplier(winnerXInput);
  const winPerUnit = getJackpotXWinPerUnitClient(winnerXInput);

  const ids = parseSessionWinnerYantraIds(winnerYantraRaw);
  let selectedBetUnits = 0;
  if (ids.length) {
    const byId = new Map(yantraWiseList.map((y) => [Number(y.yantra_id), y]));
    for (const id of ids) {
      const row = byId.get(id);
      if (row) selectedBetUnits += Number(row.bet_count) || 0;
    }
  }

  const totalBetUnits = yantraWiseList.reduce(
    (s, y) => s + (Number(y.bet_count) || 0),
    0,
  );
  const totalAmount = round2(totalBetUnits * betUnit);
  const winningPayout = round2(selectedBetUnits * winPerUnit);
  const pctRaw = Number(sessionPercentage);
  const adminPct = Number.isFinite(pctRaw)
    ? Math.min(Math.max(pctRaw, 0), 100)
    : 0;
  const adminAmount = round2((totalAmount * adminPct) / 100);
  const profitLoss = round2(totalAmount - winningPayout);
  const netProfit = round2(totalAmount - winningPayout - adminAmount);

  return {
    multiplier: mult,
    winPerUnit,
    totalBetUnits,
    totalAmount,
    selectedBetUnits,
    winningPayout,
    adminAmount,
    profitLoss,
    netProfit,
    hasMultiplier: winPerUnit > 0,
    hasWinnerYantra: ids.length > 0,
  };
}

/**
 * Profit/loss numbers for Jackpot-X (rules copy lives in the parent popover).
 */
function JackpotXProfitPreviewPanel({ preview }) {
  if (!preview) {
    return (
      <div className="ls-admin-jackpotx-profit-block">
        <div className="ls-admin-popover-subtitle">Profit / loss</div>
        <div className="ls-admin-popover-profit-hint">
          Live activity not loaded.
        </div>
      </div>
    );
  }
  if (!preview.hasMultiplier) {
    return (
      <div className="ls-admin-jackpotx-profit-block">
        <div className="ls-admin-popover-subtitle">Profit / loss</div>
        <div className="ls-admin-popover-profit-hint ls-admin-popover-profit-hint--muted">
          Enter a multiplier in the field (or tap 2x–5x) to see payout and
          profit/loss.
        </div>
      </div>
    );
  }
  return (
    <div className="ls-admin-jackpotx-profit-block">
      <div className="ls-admin-popover-subtitle">Profit / loss</div>
      {!preview.hasWinnerYantra ? (
        <div className="ls-admin-popover-profit-hint">
          No winner yantra on this session yet — payout preview uses{" "}
          <strong>0</strong> winning units until a default/manual winner is set.
        </div>
      ) : null}
      <div className="ls-admin-popover-profit ls-admin-jackpotx-preview ls-admin-jackpotx-preview--rows">
        <div className="ls-admin-popover-profit-row">
          <span>
            Win / unit (
            {preview.multiplier <= 10
              ? `${preview.multiplier}x`
              : `${preview.multiplier} (legacy)`}
            )
          </span>
          <span>₹{preview.winPerUnit.toFixed(0)}</span>
        </div>
        <div className="ls-admin-popover-profit-row">
          <span>Winning units</span>
          <span>{preview.selectedBetUnits}</span>
        </div>
        <div className="ls-admin-popover-profit-row">
          <span>Payout</span>
          <span>₹{preview.winningPayout.toFixed(2)}</span>
        </div>
        <div className="ls-admin-popover-profit-row">
          <span>Profit/Loss</span>
          <span
            className={`ls-profit-v${preview.profitLoss >= 0 ? " pos" : " neg"}`}
          >
            ₹{preview.profitLoss.toFixed(1)}
          </span>
        </div>
        <div className="ls-admin-popover-profit-row">
          <span>Net profit</span>
          <span
            className={`ls-profit-v${preview.netProfit >= 0 ? " pos" : " neg"}`}
          >
            ₹{preview.netProfit.toFixed(1)}
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * Pool preview for MANUAL (one yantra) or JACKPOT (multi): matches server non-JACKPOTX settlement
 * (total pool = Σ bet_count × yantra_amount; payout = winning bet units × winning_amount).
 */
function computePoolProfitPreview(
  yantraWiseList,
  selectedWinningBetUnits,
  settlementSettings,
  sessionPercentage,
  hasSelection,
) {
  if (!Array.isArray(yantraWiseList) || yantraWiseList.length === 0)
    return null;
  const round2 = (n) => {
    const x = Number(n);
    if (!Number.isFinite(x)) return 0;
    return Math.round((x + Number.EPSILON) * 100) / 100;
  };
  const betUnit =
    Number(settlementSettings?.yantra_amount) > 0
      ? Number(settlementSettings.yantra_amount)
      : 11;
  const winUnit =
    Number(settlementSettings?.winning_amount) > 0
      ? Number(settlementSettings.winning_amount)
      : 100;
  const totalBetUnits = yantraWiseList.reduce(
    (s, y) => s + (Number(y.bet_count) || 0),
    0,
  );
  const totalAmount = round2(totalBetUnits * betUnit);
  const selectedBetUnits = Math.max(0, Number(selectedWinningBetUnits) || 0);
  const winningPayout = round2(selectedBetUnits * winUnit);
  const pctRaw = Number(sessionPercentage);
  const adminPct = Number.isFinite(pctRaw)
    ? Math.min(Math.max(pctRaw, 0), 100)
    : 0;
  const adminAmount = round2((totalAmount * adminPct) / 100);
  const profitLoss = round2(totalAmount - winningPayout);
  const netProfit = round2(totalAmount - winningPayout - adminAmount);
  return {
    totalBetUnits,
    totalAmount,
    selectedBetUnits,
    winningPayout,
    adminAmount,
    profitLoss,
    netProfit,
    hasSelection: !!hasSelection,
  };
}

function AdminPopoverProfitPreview({ preview, noDataHint, noSelectionHint }) {
  return (
    <div className="ls-admin-popover-profit">
      {!preview ? (
        <div className="ls-admin-popover-profit-hint">{noDataHint}</div>
      ) : !preview.hasSelection ? (
        <div className="ls-admin-popover-profit-hint">{noSelectionHint}</div>
      ) : (
        <>
          <div className="ls-admin-popover-profit-row">
            <span>Winning units</span>
            <span>{preview.selectedBetUnits}</span>
          </div>
          <div className="ls-admin-popover-profit-row">
            <span>Payout</span>
            <span>₹{preview.winningPayout.toFixed(2)}</span>
          </div>
          <div className="ls-admin-popover-profit-row">
            <span>Profit/Loss</span>
            <span
              className={`ls-profit-v${preview.profitLoss >= 0 ? " pos" : " neg"}`}
            >
              ₹{preview.profitLoss.toFixed(1)}
            </span>
          </div>
          <div className="ls-admin-popover-profit-row">
            <span>Net profit</span>
            <span
              className={`ls-profit-v${preview.netProfit >= 0 ? " pos" : " neg"}`}
            >
              ₹{preview.netProfit.toFixed(1)}
            </span>
          </div>
        </>
      )}
    </div>
  );
}

const DEFAULT_BET_CLOSE_SECONDS = 10;

const LiveSession = () => {
  const liveSSApi = new LiveSessionServices();
  const settingApi = new SettingServices();
  const jackpotWinnerApi = new JackpotWinnerServices();
  const jackpotXWinnerApi = new JackpotXWinnerServices();
  const manualWinnerApi = new ManualWinnerServices();
  const routerHistory = useHistory();
  // Tabs, and which game is showing, come from whichever games are
  // running. `activeTab` stays the game_code so the rest of this file -
  // sockets, fetches, storage - is unchanged.
  const {
    games: activeGames,
    loading: gamesLoading,
    activeCode: activeTab,
    setActiveCode: setActiveTab,
    activeGame,
    totalGhar,
    reload: reloadGames,
  } = useActiveGames();
  const [socket, setSocket] = useState(null);
  const [sessionId, setSessionId] = useState(null); // session_uid (string)
  const [sessionDbId, setSessionDbId] = useState(null); // numeric game_sessions.id
  const [result, setResult] = useState(null);
  const [sessionStatus, setSessionStatus] = useState("live");
  const [loading, setLoading] = useState(false);
  const [yantraLoading, setYantraLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [yantraLength, setYantraLength] = useState(10);

  const { user } = useContext(AuthContext);
  const [profile, setProfile] = useState(user);

  // Super Admin (1) and Admin (2) only view live activity; no betting
  const isViewOnly = user?.role_id === 1 || (user?.role_id === 2 || user?.role_id === 5);

  /** Avoid stale closures in intervals / async callbacks (Super Admin profit/loss + live activity poll). */
  const profileRoleIdRef = useRef(Number(user?.role_id));
  const sessionDbIdRef = useRef(null);

  const [liveActivity, setLiveActivity] = useState(null);
  const [liveActivityLoading, setLiveActivityLoading] = useState(false);
  const [applyManualWinnerLoading, setApplyManualWinnerLoading] =
    useState(false);
  const [applyJackpotXWinnerLoading, setApplyJackpotXWinnerLoading] =
    useState(false);

  const [winnerX, setWinnerX] = useState("");
  /** Grid slot 1..N for Jackpot-X winner (maps to liveActivity.yantraWise[slot-1]). */
  const [jackpotXGridSlot, setJackpotXGridSlot] = useState(0);
  const [manualAllowedButtons, setManualAllowedButtons] = useState(0);
  const [showManualButtonsPicker, setShowManualButtonsPicker] = useState(false);
  const [showMultiWinnersPicker, setShowMultiWinnersPicker] = useState(false);
  const multiWinnersPickerRef = useRef(null);
  const [showJackpotXPicker, setShowJackpotXPicker] = useState(false);
  const jackpotXPickerRef = useRef(null);
  const [applyMultiWinnerLoading, setApplyMultiWinnerLoading] = useState(false); // [2, 3, 4]
  const [selectedMultiWinners, setSelectedMultiWinners] = useState([]); // [{num: 2, yantra_id: 1}, {num: 3, yantra_id: 2}, {num: 4, yantra_id: 3}]

  const [profitLossData, setProfitLossData] = useState(null);
  const [profitLossLoading, setProfitLossLoading] = useState(false);
  const profitLossInFlightRef = useRef(false);

  /** Super Admin: per-user yantra 1–10 purchase grid for current session (synced via socket + poll). */
  const [superAdminPurchaseGrid, setSuperAdminPurchaseGrid] = useState(null);
  const superAdminPurchaseFetchGenRef = useRef(0);

  /** Super Admin: global online counts (live Socket.IO sessions), same as dashboard. */
  const [lsOnlinePayload, setLsOnlinePayload] = useState({
    counts: { admin: 0, outlet: 0, mobile: 0 },
    users: { admin: [], outlet: [], mobile: [] },
  });
  const [lsOnlineModalRole, setLsOnlineModalRole] = useState(null);
  const lsOnlineAbortRef = useRef(null);

  const loadLsOnlineUsers = useCallback(async () => {
    if (profileRoleIdRef.current !== 1) return;
    try {
      lsOnlineAbortRef.current?.abort();
      const ac = new AbortController();
      lsOnlineAbortRef.current = ac;
      const res = await DashboardServices.getOnlineUsersByRole({
        signal: ac.signal,
      });
      if (res?.isSuccess && res?.data) {
        setLsOnlinePayload({
          counts: res.data.counts || {
            admin: 0,
            outlet: 0,
            mobile: 0,
          },
          users: res.data.users || {
            admin: [],
            outlet: [],
            mobile: [],
          },
        });
      }
    } catch (e) {
      if (e?.name === "AbortError" || e?.code === "ERR_CANCELED") return;
      console.error("loadLsOnlineUsers:", e);
    }
  }, []);

  const loadLsOnlineUsersRef = useRef(loadLsOnlineUsers);

  const refreshProfitLoss = async (opts = {}) => {
    const { silent = true, sessionDbIdOverride } = opts;
    const sid = sessionDbIdOverride ?? sessionDbIdRef.current ?? sessionDbId;
    if (!sid || profileRoleIdRef.current !== 1) return;
    if (profitLossInFlightRef.current) return;
    profitLossInFlightRef.current = true;
    setProfitLossLoading(true);
    try {
      const res = await liveSSApi.getProfitLoss(sid);
      if (res?.isSuccess && res?.data) {
        setProfitLossData(res.data);
      } else if (!silent) {
        toast.error(res?.message || "Failed to load profit/loss");
      }
    } catch (err) {
      if (!silent)
        toast.error(
          err?.response?.data?.message || "Failed to load profit/loss",
        );
    } finally {
      setProfitLossLoading(false);
      profitLossInFlightRef.current = false;
    }
  };

  const fetchSuperAdminPurchaseGrid = async () => {
    const sid = sessionDbIdRef.current;
    if (!sid || profileRoleIdRef.current !== 1) return;
    const gen = ++superAdminPurchaseFetchGenRef.current;
    try {
      const res = await liveSSApi.getSuperAdminSessionPurchaseGrid(sid);
      if (gen !== superAdminPurchaseFetchGenRef.current) return;
      if (res?.isSuccess && res?.data) {
        setSuperAdminPurchaseGrid(res.data);
      } else {
        setSuperAdminPurchaseGrid(emptySuperAdminPurchaseGrid(sid));
      }
    } catch (err) {
      console.error("fetchSuperAdminPurchaseGrid:", err);
      if (gen !== superAdminPurchaseFetchGenRef.current) return;
      setSuperAdminPurchaseGrid(emptySuperAdminPurchaseGrid(sid));
    }
  };

  const fetchSuperAdminPurchaseGridRef = useRef(fetchSuperAdminPurchaseGrid);

  const manualPickerRef = useRef(null);
  /**
   * Mirrors what the server settles with, for the on-screen profit preview.
   * The running round's snapshot comes first (an edit to the game applies
   * from the next round, so the game row can be ahead of the round in
   * play); the game's own amounts are the fallback before a round loads.
   */
  const settlementSettings = useMemo(
    () => ({
      yantra_amount:
        Number(liveActivity?.yantra_amount) ||
        Number(activeGame?.yantra_amount) ||
        0,
      winning_amount:
        Number(liveActivity?.winning_amount) ||
        Number(activeGame?.winning_amount) ||
        0,
    }),
    [
      liveActivity?.yantra_amount,
      liveActivity?.winning_amount,
      activeGame?.yantra_amount,
      activeGame?.winning_amount,
    ],
  );

  useEffect(() => {
    if (user) setProfile(user);
  }, [user]);

  useEffect(() => {
    profileRoleIdRef.current = Number(profile?.role_id);
  }, [profile?.role_id]);

  useEffect(() => {
    sessionDbIdRef.current = sessionDbId;
  }, [sessionDbId]);

  // Reload the game list when Super Admin starts, stops or edits a game,
  // so the tabs and the amounts used for the preview stay current.
  useEffect(() => {
    const s = io(process.env.REACT_APP_NODE_URL, {
      transports: ["websocket", "polling"],
      reconnection: true,
    });
    s.on("gamesChanged", () => reloadGames());
    return () => s.disconnect();
  }, [reloadGames]);

  const endTimeRef = useRef(null);
  const endTimeStrRef = useRef(null);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  // "Bet Close Before End" of the running game (seconds), from the round
  // payload - bets stop and "Winner processing" shows for exactly this long.
  const [betCloseSeconds, setBetCloseSeconds] = useState(
    DEFAULT_BET_CLOSE_SECONDS,
  );
  const [currentTimeDisplay, setCurrentTimeDisplay] = useState("");
  const timerRef = useRef(null);
  const reloadInFlightRef = useRef(false);
  const lastReloadAtRef = useRef(0);
  const activeTabRef = useRef(activeTab);
  const sessionStatusRef = useRef(sessionStatus);

  // 12-hour format for display (e.g. "4:36 PM" instead of "16:36 PM")
  const TIME_DISPLAY_FORMAT = "h:mm A";

  /**
   * Parse end_time from API. Server sends UTC (e.g. "2026-02-02T05:36:00.000Z").
   * We keep the moment in UTC and convert to local only when computing remaining or displaying.
   */
  const parseEndTime = (endTimeStr) => {
    if (!endTimeStr) return null;
    const s = String(endTimeStr).trim();
    if (!s) return null;
    const hasUtcMarker = s.endsWith("Z") || s.includes("T");
    const m = hasUtcMarker ? moment.utc(s) : moment(s);
    return m.isValid() ? { endM: m, isUTC: hasUtcMarker } : null;
  };

  /**
   * Remaining seconds until session end. Must be computed ONLY from backend `end_time`.
   * Use moment(end_time).diff(moment(), "seconds") as single source of truth.
   */
  const getRemainingSecondsFromEndTime = (endTimeStr) => {
    if (!endTimeStr) return 0;
    const secs = moment(endTimeStr).diff(moment(), "seconds");
    return Math.max(Number.isFinite(secs) ? secs : 0, 0);
  };

  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);
  useEffect(() => {
    setStoredActiveGameTab(activeTab);
  }, [activeTab]);

  useEffect(() => {
    sessionStatusRef.current = sessionStatus;
  }, [sessionStatus]);

  const [initialValues, setInitialValues] = useState({
    wallet_balance: 0,
    paid_amount: 0,
    total_bet: 0,
    amount: 0,
    date: "",
    draw_time: "",
    remainning_time: 0,
    current_time: "",
    session_id: "",
    session_uid: "",
    session_status: "",
    game_type: "",
    start_time: "",
    end_time: "",
    percentage: "",
    winner_type: "",
    winner_count: 0,
    session_no: "",
    yantra_list: [
      {
        yantra_id: 0,
        yantra_img: null,
        yantra_name: null,
        yantra_amount: null,
        yantra_count: 0,
      },
    ],
  });

  /** Current session winner row(s) from live activity, in DB order (multi-winner). */
  const sessionWinnerYantraCards = useMemo(() => {
    const list = liveActivity?.yantraWise;
    if (!Array.isArray(list) || list.length === 0) return [];
    const raw =
      liveActivity?.winner_yantra ??
      profitLossData?.selectedYantra ??
      initialValues?.winner_yantra;
    const ids = parseSessionWinnerYantraIds(raw);
    if (!ids.length) return [];
    const byId = new Map(list.map((y) => [Number(y.yantra_id), y]));
    return ids.map((id) => byId.get(id)).filter(Boolean);
  }, [
    liveActivity?.yantraWise,
    liveActivity?.winner_yantra,
    profitLossData?.selectedYantra,
    initialValues?.winner_yantra,
  ]);

  /** Ids of the session winner(s), for marking the tiles on the board. */
  const sessionWinnerIdSet = useMemo(
    () => new Set(sessionWinnerYantraCards.map((y) => Number(y.yantra_id))),
    [sessionWinnerYantraCards],
  );

  /** "SIMPAL", "Multi Winner", "JACKPOT (2x)" ... whichever source knows it. */
  const sessionWinnerTypeLabel = useMemo(() => {
    const pick = (type, x) => {
      if (!type) return "";
      if (type === "JACKPOT") return "Multi Winner";
      if (type === "JACKPOTX") return `JACKPOT (${x})`;
      return type;
    };
    return (
      pick(profitLossData?.winnerType, profitLossData?.winner_x) ||
      pick(liveActivity?.winner_type, liveActivity?.winner_x) ||
      pick(initialValues?.winner_type, initialValues?.winner_x) ||
      ""
    );
  }, [profitLossData, liveActivity, initialValues]);

  /** Preview profit/loss for multi-winner (JACKPOT) using live yantraWise bet_count + selected positions. */
  const multiWinnerPickProfitPreview = useMemo(() => {
    const list = liveActivity?.yantraWise;
    if (!Array.isArray(list) || list.length === 0) return null;

    const idSet = new Set(
      (selectedMultiWinners || [])
        .map((s) => Number(s.yantra_id))
        .filter((n) => Number.isFinite(n) && n > 0),
    );

    let selectedBetUnits = 0;
    if (idSet.size > 0) {
      for (const y of list) {
        const yid = Number(y.yantra_id);
        if (idSet.has(yid)) {
          selectedBetUnits += Number(y.bet_count) || 0;
        }
      }
    }

    return computePoolProfitPreview(
      list,
      selectedBetUnits,
      settlementSettings,
      initialValues?.percentage,
      idSet.size > 0,
    );
  }, [
    liveActivity?.yantraWise,
    selectedMultiWinners,
    settlementSettings.yantra_amount,
    settlementSettings.winning_amount,
    initialValues?.percentage,
  ]);

  /** Preview for manual winner: grid position 1..N → yantraWise[row] bet_count (same order as multi grid). */
  const manualWinnerPickProfitPreview = useMemo(() => {
    const list = liveActivity?.yantraWise;
    if (!Array.isArray(list) || list.length === 0) return null;
    const num = Number(manualAllowedButtons);
    const row = num >= 1 ? list[num - 1] : undefined;
    const selectedBetUnits = row ? Number(row.bet_count) || 0 : 0;
    const hasSelection = num >= 1 && !!row;
    return computePoolProfitPreview(
      list,
      selectedBetUnits,
      settlementSettings,
      initialValues?.percentage,
      hasSelection,
    );
  }, [
    liveActivity?.yantraWise,
    manualAllowedButtons,
    settlementSettings.yantra_amount,
    settlementSettings.winning_amount,
    initialValues?.percentage,
  ]);

  /** Resolved DB yantra id for Jackpot-X apply / preview from current grid slot. */
  const jackpotXResolvedYantraId = useMemo(() => {
    const list = liveActivity?.yantraWise;
    const slot = Number(jackpotXGridSlot) || 0;
    if (slot < 1) return 0;
    const row = Array.isArray(list) ? list[slot - 1] : undefined;
    const yid = Number(row?.yantra_id);
    return Number.isFinite(yid) && yid > 0 ? yid : slot;
  }, [liveActivity?.yantraWise, jackpotXGridSlot]);

  /**
   * Resolved DB yantra id for the Manual Winner apply, from the chosen grid
   * slot.
   *
   * The picker is a grid of positions 1..N. That happened to equal the
   * yantra id while every game shared the yantras 1-10, but a board is a
   * game's own list now: the 15 Min board is ids 17-26, so slot 5 there is
   * Hanuman, not the id 5 that would land on 3 Min's Love Yantra. Jackpot-X
   * already resolves its slot this way; Manual Winner was still sending the
   * raw position.
   */
  const manualResolvedYantraId = useMemo(() => {
    const list = liveActivity?.yantraWise;
    const slot = Number(manualAllowedButtons) || 0;
    if (slot < 1) return 0;
    const row = Array.isArray(list) ? list[slot - 1] : undefined;
    const yid = Number(row?.yantra_id);
    return Number.isFinite(yid) && yid > 0 ? yid : 0;
  }, [liveActivity?.yantraWise, manualAllowedButtons]);

  /** JACKPOTX preview: multiplier from winnerX; yantra from selected slot or session. */
  const jackpotXPickProfitPreview = useMemo(() => {
    const list = liveActivity?.yantraWise;
    let winnerRaw =
      liveActivity?.winner_yantra ??
      initialValues?.winner_yantra ??
      profitLossData?.selectedYantra;
    if (jackpotXGridSlot >= 1) {
      const row = Array.isArray(list) ? list[jackpotXGridSlot - 1] : undefined;
      const yid = Number(row?.yantra_id);
      winnerRaw = String(
        Number.isFinite(yid) && yid > 0 ? yid : jackpotXGridSlot,
      );
    }
    return computeJackpotXProfitPreview(
      list,
      winnerRaw,
      winnerX,
      settlementSettings,
      initialValues?.percentage,
    );
  }, [
    liveActivity?.yantraWise,
    liveActivity?.winner_yantra,
    initialValues?.winner_yantra,
    profitLossData?.selectedYantra,
    jackpotXGridSlot,
    winnerX,
    settlementSettings.yantra_amount,
    settlementSettings.winning_amount,
    initialValues?.percentage,
  ]);

  // Fetch today's last draw list for the active game type
  const fetchTodayLastDraw = async (gameType = activeTab) => {
    // No game selected yet (fresh login, list still loading) - nothing to ask for.
    if (!gameType) return;
    try {
      setHistoryLoading(true);
      const response = await liveSSApi.getTodayLastDraw(gameType);
      if (response?.isSuccess && Array.isArray(response?.data)) {
        setHistory(response.data);
      } else {
        setHistory([]);
      }
    } catch (error) {
      console.error("Error in fetchTodayLastDraw:", error);
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchLiveActivity = async (gameType = activeTab) => {
    if (!isViewOnly) return;
    try {
      setLiveActivityLoading(true);
      const response = await liveSSApi.getLiveActivity(gameType);
      if (response?.isSuccess && response?.data) {
        setLiveActivity(response.data);
        // Super Admin: profit/loss uses same bet data as this API; refresh whenever live totals update
        if (profileRoleIdRef.current === 1 && response.data.sessionDbId) {
          refreshProfitLoss({
            silent: true,
            sessionDbIdOverride: response.data.sessionDbId,
          });
        }
      } else {
        setLiveActivity(null);
      }
    } catch (error) {
      console.error("Error in fetchLiveActivity:", error);
      setLiveActivity(null);
    } finally {
      setLiveActivityLoading(false);
    }
  };

  const fetchLiveActivityRef = useRef(fetchLiveActivity);
  const refreshProfitLossRef = useRef(refreshProfitLoss);
  useEffect(() => {
    fetchLiveActivityRef.current = fetchLiveActivity;
    refreshProfitLossRef.current = refreshProfitLoss;
    fetchSuperAdminPurchaseGridRef.current = fetchSuperAdminPurchaseGrid;
    loadLsOnlineUsersRef.current = loadLsOnlineUsers;
  });

  useEffect(() => {
    if (Number(profile?.role_id) !== 1) {
      lsOnlineAbortRef.current?.abort();
      setLsOnlineModalRole(null);
      return undefined;
    }
    loadLsOnlineUsers();
    const t = setInterval(() => {
      loadLsOnlineUsersRef.current();
    }, LS_ONLINE_POLL_MS);
    return () => {
      clearInterval(t);
      lsOnlineAbortRef.current?.abort();
    };
  }, [profile?.role_id, loadLsOnlineUsers]);

  useEffect(() => {
    if (!sessionId || !sessionDbId) return;

    const socket = io(process.env.REACT_APP_NODE_URL, {
      transports: ["websocket", "polling"],
      reconnection: true,
    });

    socket.on("connect", () => {
      socket.emit("joinSession", {
        sessionId, // session_uid (room id)
        sessionDbId, // numeric game_sessions.id
        userId: profile?.user_id,
        username: profile?.name,
        role: profile?.role,
        gameType: activeTab,
      });
      // Fallback snapshot (socket pushes handle realtime updates)
      if (isViewOnly) {
        fetchLiveActivityRef.current(activeTabRef.current);
        refreshProfitLossRef.current({ silent: true });
        if (profileRoleIdRef.current === 1 && sessionDbIdRef.current) {
          fetchSuperAdminPurchaseGridRef.current();
        }
        if (profileRoleIdRef.current === 1) {
          loadLsOnlineUsersRef.current();
        }
      }
    });

    socket.on("liveActivityUpdated", (payload) => {
      if (!payload?.sessionId || payload.sessionId !== sessionId) return;
      if (payload.gameType && payload.gameType !== activeTabRef.current) return;
      setLiveActivity(payload);
      if (
        profileRoleIdRef.current === 1 &&
        Number(payload.sessionDbId) === Number(sessionDbIdRef.current)
      ) {
        fetchSuperAdminPurchaseGridRef.current();
      }
      if (profileRoleIdRef.current === 1) {
        loadLsOnlineUsersRef.current();
      }
    });

    socket.on("profitLossUpdated", (payload) => {
      if (profileRoleIdRef.current !== 1) return;
      if (
        !payload?.sessionDbId ||
        Number(payload.sessionDbId) !== Number(sessionDbIdRef.current)
      ) {
        return;
      }
      setProfitLossData(payload);
    });

    socket.on("resultAnnounced", (data) => {
      setResult(data);
    });

    socket.on("newRound", (data) => {
      setResult(null);
      getLiveSessionData(activeTabRef.current, { silent: true });
      fetchTodayLastDraw(activeTabRef.current);
      if (isViewOnly) fetchLiveActivity(activeTabRef.current);
      refreshProfitLoss({ silent: true });
      if (profileRoleIdRef.current === 1 && sessionDbIdRef.current) {
        fetchSuperAdminPurchaseGridRef.current();
      }
      if (profileRoleIdRef.current === 1) {
        loadLsOnlineUsersRef.current();
      }
    });

    socket.on("sessionEnded", () => {
      getLiveSessionData(activeTabRef.current, { silent: true });
      fetchTodayLastDraw(activeTabRef.current);
      if (isViewOnly) fetchLiveActivity(activeTabRef.current);
      refreshProfitLoss({ silent: true });
      if (profileRoleIdRef.current === 1 && sessionDbIdRef.current) {
        fetchSuperAdminPurchaseGridRef.current();
      }
      if (profileRoleIdRef.current === 1) {
        loadLsOnlineUsersRef.current();
      }
    });

    socket.on("winnerUpdated", (payload) => {
      // room is session_uid; ignore unrelated sessions
      if (payload?.session_uid && payload.session_uid !== sessionId) return;

      const wt = payload?.winner_type || payload?.winnerType || "";
      const wy = payload?.winner_yantra || payload?.winnerYantra || "";
      const wc = payload?.winner_count ?? payload?.winnerCount ?? null;
      const wx = payload?.winner_x ?? payload?.winnerX ?? null;

      if (wt || wy || wc != null || wx != null) {
        setInitialValues((prev) => ({
          ...prev,
          ...(wt ? { winner_type: wt } : {}),
          ...(wy != null ? { winner_yantra: wy } : {}),
          ...(wc != null ? { winner_count: wc } : {}),
          ...(wx != null ? { winner_x: wx } : {}),
        }));
        // Do not set `winnerX` here — it is the Apply form field and must stay
        // empty after Apply; socket would otherwise refill it from winner_x.
      }

      // Server emits liveActivityUpdated + profitLossUpdated after winner apply.
      // No toast here: Apply actions already show the API message (e.g. "Jackpot
      // Winner set successfully"); a second toast would duplicate it.
    });

    setSocket(socket);
    return () => socket.disconnect();
  }, [
    sessionId,
    sessionDbId,
    profile?.user_id,
    profile?.name,
    profile?.role,
    activeTab,
    isViewOnly,
  ]);

  useEffect(() => {
    refreshProfitLoss({ silent: true });
  }, [sessionDbId, profile?.role_id]);

  useEffect(() => {
    if (Number(profile?.role_id) !== 1 || !sessionDbId) {
      setSuperAdminPurchaseGrid(null);
      return;
    }
    setSuperAdminPurchaseGrid(emptySuperAdminPurchaseGrid(sessionDbId));
    fetchSuperAdminPurchaseGridRef.current();
  }, [sessionDbId, profile?.role_id]);

  // Refresh last draw list when game type tab changes
  useEffect(() => {
    fetchTodayLastDraw(activeTab);
  }, [activeTab]);

  // View-only: fetch live activity on tab change
  useEffect(() => {
    if (isViewOnly) fetchLiveActivity(activeTab);
  }, [isViewOnly, activeTab]);

  // View-only: fetch when session becomes available and poll every 5s when RUNNING
  useEffect(() => {
    if (!isViewOnly || !sessionId) return;
    fetchLiveActivity(activeTabRef.current);
    if (sessionStatus !== "RUNNING") return;
    // Slow fallback if socket events miss (primary updates: liveActivityUpdated / profitLossUpdated)
    const interval = setInterval(() => {
      fetchLiveActivity(activeTabRef.current);
      if (profileRoleIdRef.current === 1 && sessionDbIdRef.current) {
        fetchSuperAdminPurchaseGridRef.current();
      }
      if (profileRoleIdRef.current === 1) {
        loadLsOnlineUsersRef.current();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [isViewOnly, sessionId, sessionStatus, profile?.role_id]);

  async function getLiveSessionData(gameType = activeTab, opts = {}) {
    const { silent = false, keepCurrentOnEmpty = false } = opts;
    if (!gameType) return;

    setLoading(true);
    liveSSApi
      .getLiveSessionData(gameType)
      .then((response) => {
        if (response.isSuccess && response?.data) {
          const data = response?.data;
          const walletData = response?.walletData;
          const walletBalance = walletData?.wallet_balance ?? 0;

          // Backend sends `paid_amoun` (typo) in some APIs. Support both keys.
          const paidAmount =
            walletData?.paid_amount ?? walletData?.paid_amoun ?? 0;
          const parsedEnd = parseEndTime(data?.end_time);
          endTimeRef.current = parsedEnd;
          endTimeStrRef.current = data?.end_time || null;

          setSessionId(data.session_uid);
          setSessionDbId(data.id);
          setSessionStatus(data.status || "live");

          const start = moment.utc(data?.start_time);
          const endParsed = parsedEnd;
          const endM = endParsed ? endParsed.endM : moment.utc(data?.end_time);
          const date = start.isValid()
            ? start.local().format("DD-MM-YYYY")
            : moment().format("DD-MM-YYYY");
          const drawTime = endM.isValid()
            ? endM.clone().local().format(TIME_DISPLAY_FORMAT)
            : "";
          const currentTime = moment().local().format(TIME_DISPLAY_FORMAT);
          const remaining = getRemainingSecondsFromEndTime(data?.end_time);
          const closeSecs = Number(data?.bet_close_seconds);
          setBetCloseSeconds(
            Number.isFinite(closeSecs) && closeSecs > 0
              ? closeSecs
              : DEFAULT_BET_CLOSE_SECONDS,
          );
          setRemainingSeconds(remaining);
          setCurrentTimeDisplay(currentTime);

          setInitialValues((prev) => ({
            ...prev,
            wallet_balance: walletBalance,
            paid_amount: paidAmount,
            date,
            draw_time: drawTime,
            current_time: currentTime,
            remainning_time: remaining,
            session_id: data?.id || "",
            session_uid: data?.session_uid || "",
            session_status: data?.status || "",
            game_type: data?.game_type || "",
            start_time: data?.start_time || "",
            end_time: data?.end_time || "",
            percentage: data?.percentage || "",
            winner_type: data?.winner_type || "",
            winner_count: data?.winner_count || 0,
            session_no: data?.session_no || "",
          }));

          // Also refresh today's last-draw list whenever we successfully
          // load a running session (handles auto-load after completion).
          fetchTodayLastDraw(gameType);
        } else {
          // When auto-reloading between sessions there can be a small gap where
          // backend hasn't marked the next session RUNNING yet. In that case,
          // keep current session on screen and keep polling (no flicker).
          if (!keepCurrentOnEmpty) {
            endTimeStrRef.current = null;
            endTimeRef.current = null;
            setSessionId(null);
            setCurrentTimeDisplay("");
          }
          setRemainingSeconds(0);
          if (!silent) toast.error(response?.message || "No running session");
        }
      })
      .catch((error) => {
        console.error("Error get getLiveSessionData:", error);
        // Same rule as above: on silent auto-refresh keep current UI and retry.
        if (!silent && !keepCurrentOnEmpty) {
          endTimeStrRef.current = null;
          endTimeRef.current = null;
          setSessionId(null);
          setRemainingSeconds(0);
          setCurrentTimeDisplay("");
        } else if (!silent) {
          setRemainingSeconds(0);
        }
        if (!silent) {
          toast.dismiss();
          toast.error(
            error?.response?.status === 404
              ? "No running session"
              : "Error get game sessions",
          );
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }

  const getYantraData = async (gameCode = activeTab) => {
    if (!gameCode) return;
    setYantraLoading(true);
    liveSSApi
      .getYantraListData({ game: gameCode })
      .then((response) => {
        if (response.isSuccess) {
          const yantras = response?.data || [];
          setYantraLength(yantras.length);
          const mappedYantras = yantras.map((y) => ({
            id: y.id,
            yantra_id: y.id,
            yantra_name: y.yantra_name,
            yantra_img: y.yantra_img,
            yantra_amount: y.amount,
            yantra_count: 0,
          }));

          setInitialValues((prev) => ({
            ...prev,
            yantra_list: mappedYantras,
          }));
        } else {
          toast.error(response.message);
        }
      })
      .catch((error) => {
        console.error("Error get getYantraListData:", error);
        toast.dismiss();
        toast.error("Error get yantra list data");
      })
      .finally(() => {
        setYantraLoading(false);
      });
  };

  // A game is live because its own row says so, not because it is one of
  // two known codes.
  const isCurrentGameLive = Boolean(activeGame);

  /** Column set for the purchase grid: this game's board, in board order. */
  const purchaseGridYantraIds =
    superAdminPurchaseGrid?.yantra_ids?.length > 0
      ? superAdminPurchaseGrid.yantra_ids
      : (initialValues?.yantra_list || []).map((y) => y.yantra_id);

  /** Board name for a column, so the header can say what "5" is on this game. */
  const purchaseGridYantraName = (id) =>
    (initialValues?.yantra_list || []).find(
      (y) => Number(y.yantra_id) === Number(id),
    )?.yantra_name || "";

  const applyJackpotWinner = () => {
    if (!isCurrentGameLive) {
      toast.error("Session is stopped. Please start the session first.");
      return;
    }
    if (selectedMultiWinners.length === 0) return;
    const payload = {
      game: activeTab,
      session_id: sessionId,
      winner_count: selectedMultiWinners.length,
      winner_yantras: selectedMultiWinners.map((s) => s.yantra_id).join(","),
    };
    setApplyMultiWinnerLoading(true);
    jackpotWinnerApi
      .createJackpotWinner(payload)
      .then((response) => {
        if (response.isSuccess) {
          toast.success(response.message);
          setShowMultiWinnersPicker(false);
        } else {
          toast.error(response.message);
        }
      })
      .finally(() => {
        setApplyMultiWinnerLoading(false);
        setSelectedMultiWinners([]);
      });
  };

  const applyManualWinner = () => {
    if (!isCurrentGameLive) {
      toast.error("Session is stopped. Please start the session first.");
      return;
    }
    if (!manualAllowedButtons) return;
    if (!manualResolvedYantraId) {
      toast.error("Could not resolve that yantra for this game. Try again.");
      return;
    }
    const payload = {
      game: activeTab,
      session_id: sessionId,
      yantra_id: manualResolvedYantraId,
    };
    setApplyManualWinnerLoading(true);
    manualWinnerApi
      .createManualWinner(payload)
      .then((response) => {
        if (response.isSuccess) {
          toast.success(response.message);
        } else {
          toast.error(response.message);
        }
      })
      .finally(() => {
        setApplyManualWinnerLoading(false);
        setShowManualButtonsPicker(false);
        setManualAllowedButtons(0);
      });
  };
  const applyJackpotXWinner = () => {
    if (!isCurrentGameLive) {
      toast.error("Session is stopped. Please start the session first.");
      return;
    }
    if (!winnerX?.trim()) return;
    const yid = jackpotXResolvedYantraId;
    if (!yid || yid <= 0) {
      toast.error("Select a winner yantra.");
      return;
    }
    const payload = {
      game: activeTab,
      session_id: sessionId,
      winner_x: winnerX.trim(),
      yantra_id: yid,
    };
    setApplyJackpotXWinnerLoading(true);
    jackpotXWinnerApi
      .createJackpotXWinner(payload)
      .then((response) => {
        if (response.isSuccess) {
          toast.success(response.message);
          setWinnerX("");
          setJackpotXGridSlot(0);
          setShowJackpotXPicker(false);
        } else {
          toast.error(response.message);
        }
      })
      .finally(() => {
        setApplyJackpotXWinnerLoading(false);
      });
  };

  useEffect(() => {
    getYantraData();
    getLiveSessionData();
  }, [activeTab]);

  useEffect(() => {
    if (!sessionId) return;

    const tick = () => {
      const endTimeStr = endTimeStrRef.current;
      const secs = getRemainingSecondsFromEndTime(endTimeStr);
      setRemainingSeconds(secs);
      setCurrentTimeDisplay(moment().format(TIME_DISPLAY_FORMAT));

      if (secs <= 0) {
        // Auto-load the next RUNNING session (silent, no flicker/toast spam).
        const now = Date.now();
        if (
          !reloadInFlightRef.current &&
          now - lastReloadAtRef.current > 2000
        ) {
          reloadInFlightRef.current = true;
          lastReloadAtRef.current = now;
          Promise.resolve()
            .then(() =>
              getLiveSessionData(activeTabRef.current, {
                silent: true,
                keepCurrentOnEmpty: true,
              }),
            )
            .finally(() => {
              reloadInFlightRef.current = false;
            });
        }
      }
    };

    // Reset any previous interval and start a fresh backend-driven countdown.
    if (timerRef.current) clearInterval(timerRef.current);
    tick();
    timerRef.current = setInterval(tick, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) return;
    if (sessionStatus === "RUNNING") return;

    // If backend marks session as not RUNNING, auto-refresh to get the next session.
    const now = Date.now();
    if (reloadInFlightRef.current || now - lastReloadAtRef.current <= 1500)
      return;

    reloadInFlightRef.current = true;
    lastReloadAtRef.current = now;
    Promise.resolve()
      .then(() => getLiveSessionData(activeTabRef.current, { silent: true }))
      .finally(() => {
        reloadInFlightRef.current = false;
      });
  }, [sessionId, sessionStatus]);

  const formatRemaining = (seconds) => {
    if (seconds == null || isNaN(seconds) || seconds < 0) return "00:00";
    const sec = Math.floor(Number(seconds));
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  };

  const GAME_TABS = activeGames.map((g) => ({
    key: g.game_code,
    label: g.game_name,
    ghar: g.total_ghar,
    rounds: g.sessions_per_day,
  }));

  useEffect(() => {
    if (!showManualButtonsPicker) return;

    const handleClickOutside = (event) => {
      if (
        manualPickerRef.current &&
        !manualPickerRef.current.contains(event.target)
      ) {
        setShowManualButtonsPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showManualButtonsPicker]);

  useEffect(() => {
    if (!showMultiWinnersPicker) return;

    const handleClickOutside = (event) => {
      if (
        multiWinnersPickerRef.current &&
        !multiWinnersPickerRef.current.contains(event.target)
      ) {
        setShowMultiWinnersPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMultiWinnersPicker]);

  useEffect(() => {
    if (!showJackpotXPicker) return;

    const handleClickOutside = (event) => {
      if (
        jackpotXPickerRef.current &&
        !jackpotXPickerRef.current.contains(event.target)
      ) {
        setShowJackpotXPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showJackpotXPicker]);

  const lsOnlineModalMeta = lsOnlineModalRole
    ? LS_ONLINE_MODAL_KEYS[lsOnlineModalRole]
    : null;
  const lsOnlineModalList =
    lsOnlineModalMeta && lsOnlinePayload?.users
      ? lsOnlinePayload.users[lsOnlineModalMeta.listKey] || []
      : [];

  return (
    <div className="live-session-container">
      <div className="live-session-header">
        <div className="live-session-tabs">
          {gamesLoading ? (
            <span className="live-session-tabs-empty">Loading games...</span>
          ) : GAME_TABS.length === 0 ? (
            <span className="live-session-tabs-empty">
              No games are running. Start one in Settings &rsaquo; Games.
            </span>
          ) : null}
          {GAME_TABS.length > MAX_GAME_TABS && (
            <GameSelect
              games={activeGames}
              value={activeTab}
              onChange={setActiveTab}
              variant="dark"
            />
          )}
          {GAME_TABS.length <= MAX_GAME_TABS && GAME_TABS.map((tab) => {
            // Only running games are listed, so every tab here is live.
            const isLive = true;
            return (
              <button
                key={tab.key}
                type="button"
                className={`live-session-tab ${activeTab === tab.key ? "active" : ""}`}
                onClick={() => setActiveTab(tab.key)}
                title={`${tab.ghar} ghar · ${tab.rounds} rounds a day`}
              >
                <span>{tab.label}</span>
                <span
                  className={`live-session-tab-status ${isLive ? "live" : "stopped"}`}
                  aria-label={isLive ? "Live" : "Stopped"}
                >
                  {isLive ? "Live" : "Stopped"}
                </span>
              </button>
            );
          })}
          {profile?.role_id === 1 && (
            <div
              className="ls-super-admin-online-wrap"
              aria-label="Online users by role"
            >
              <div className="ls-super-admin-online-title">Online</div>
              <div className="ls-super-admin-online-strip">
                <button
                  type="button"
                  className="ls-online-pill"
                  onClick={() => setLsOnlineModalRole("outlet")}
                >
                  <span className="ls-online-pill-label">Outlet</span>
                  <span className="ls-online-pill-count">
                    {lsOnlinePayload.counts.outlet ?? 0}
                  </span>
                </button>
                <button
                  type="button"
                  className="ls-online-pill"
                  onClick={() => setLsOnlineModalRole("mobile")}
                >
                  <span className="ls-online-pill-label">Mobile</span>
                  <span className="ls-online-pill-count">
                    {lsOnlinePayload.counts.mobile ?? 0}
                  </span>
                </button>
              </div>
            </div>
          )}
        </div>
        {profile?.role_id === 1 && (
          <div className="ls-admin-strip">
            {/* {activeTab === "3min" && ( */}
              <div
                className="ls-admin-block ls-admin-manual-block"
                ref={multiWinnersPickerRef}
              >
                <div className="ls-admin-label">Multi</div>
                <div className="ls-admin-inline">
                  <button
                    type="button"
                    className="ls-admin-picker-input"
                    onClick={() => setShowMultiWinnersPicker((prev) => !prev)}
                  >
                    {selectedMultiWinners.length > 0
                      ? `Multi Winner: ${selectedMultiWinners.map((s) => s.num).join(", ")}`
                      : "Select Multi Winner"}
                  </button>
                  <button
                    type="button"
                    className="ls-admin-apply-btn"
                    disabled={
                      !isCurrentGameLive ||
                      remainingSeconds <= betCloseSeconds ||
                      selectedMultiWinners.length === 0 ||
                      applyMultiWinnerLoading
                    }
                    onClick={applyJackpotWinner}
                    title={
                      !isCurrentGameLive
                        ? "Session must be Live to apply"
                        : remainingSeconds <= betCloseSeconds
                          ? `Winner cannot be changed in the last ${betCloseSeconds} seconds`
                          : undefined
                    }
                  >
                    {applyMultiWinnerLoading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Applying...
                      </>
                    ) : (
                      "Apply"
                    )}
                  </button>
                </div>

                {showMultiWinnersPicker && (
                  <div className="ls-admin-popover">
                    <div className="ls-admin-popover-title">
                      SELECT MULTI WINNER
                    </div>
                    <div className="ls-admin-popover-grid">
                      {liveActivity?.yantraWise?.length > 0 &&
                        liveActivity.yantraWise.map((y, idx) => {
                          const num = idx + 1;
                          const selected = selectedMultiWinners.some(
                            (s) => s.num === num,
                          );
                          return (
                            <button
                              key={num}
                              type="button"
                              className={`ls-admin-popover-button${
                                selected ? " selected" : ""
                              }`}
                              onClick={() => {
                                setSelectedMultiWinners((prev) => {
                                  const exists = (prev || []).some(
                                    (s) => s.num === num,
                                  );
                                  if (exists) {
                                    return (prev || []).filter(
                                      (s) => s.num !== num,
                                    );
                                  }
                                  const yantraId =
                                    y?.yantra_id ??
                                    y?.id ??
                                    y?.yantraId ??
                                    null;
                                  return [
                                    ...(prev || []),
                                    { num, yantra_id: yantraId },
                                  ];
                                });
                              }}
                            >
                              {num}
                            </button>
                          );
                        })}
                    </div>
                    <AdminPopoverProfitPreview
                      preview={multiWinnerPickProfitPreview}
                      noDataHint="Live activity not loaded."
                      noSelectionHint="Select position(s) to preview payout and profit/loss (uses bet counts per yantra)."
                    />
                  </div>
                )}
              </div>
            {/* )} */}
            <div
              className="ls-admin-block ls-admin-manual-block"
              ref={jackpotXPickerRef}
            >
              <div className="ls-admin-label">
                Jackpot Winner
                {jackpotXGridSlot >= 1 && (
                  <span className="ls-admin-jackpotx-yantra-hint">
                    {" "}
                    · Yantra #{jackpotXResolvedYantraId}
                  </span>
                )}
              </div>
              <div className="ls-admin-inline ls-admin-jackpotx-inline-row">
                <div className="ls-admin-jackpotx-anchor">
                  <div
                    className={`ls-admin-jackpotx-input-wrap${showJackpotXPicker ? " is-open" : ""}`}
                  >
                    <input
                      type="text"
                      placeholder="e.g. 2x, 3x"
                      className="ls-admin-jackpotx-input"
                      value={winnerX}
                      onChange={(e) => setWinnerX(e.target.value)}
                      onFocus={() => setShowJackpotXPicker(true)}
                      onBlur={() => setWinnerX((v) => String(v).trim())}
                      aria-label="Jackpot-X multiplier"
                      aria-expanded={showJackpotXPicker}
                      autoComplete="off"
                      spellCheck="false"
                    />
                  </div>

                  {showJackpotXPicker && (
                    <div
                      className="ls-admin-popover ls-admin-popover-jackpotx"
                      role="dialog"
                      aria-label="Jackpot-X multiplier and profit preview"
                    >
                      <div className="ls-admin-popover-title">
                        SELECT JACKPOT-X
                      </div>
                      <div className="ls-admin-popover-grid ls-admin-popover-grid-jackpotx">
                        {JACKPOTX_QUICK_OPTIONS.map((opt) => {
                          const picked =
                            normalizeJackpotXInput(winnerX) ===
                            normalizeJackpotXInput(opt);
                          return (
                            <button
                              key={opt}
                              type="button"
                              className={`ls-admin-popover-button${picked ? " selected" : ""}`}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => {
                                setWinnerX(opt);
                              }}
                            >
                              {opt}
                            </button>
                          );
                        })}
                      </div>
                      <div className="ls-admin-popover-title">
                        SELECT YANTRA (ONE)
                      </div>
                      <div className="ls-admin-popover-grid">
                        {Array.from({ length: yantraLength }).map((_, idx) => {
                          const num = idx + 1;
                          const selected = jackpotXGridSlot === num;
                          return (
                            <button
                              key={num}
                              type="button"
                              className={`ls-admin-popover-button${
                                selected ? " selected" : ""
                              }`}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => setJackpotXGridSlot(num)}
                            >
                              {num}
                            </button>
                          );
                        })}
                      </div>
                      <div className="ls-admin-jackpotx-popover-divider" />
                      <JackpotXProfitPreviewPanel
                        preview={jackpotXPickProfitPreview}
                      />
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="ls-admin-apply-btn"
                  disabled={
                    !isCurrentGameLive ||
                    remainingSeconds <= betCloseSeconds ||
                    !winnerX?.trim() ||
                    jackpotXGridSlot < 1 ||
                    applyJackpotXWinnerLoading
                  }
                  onClick={applyJackpotXWinner}
                  title={
                    !isCurrentGameLive
                      ? "Session must be Live to apply"
                      : remainingSeconds <= betCloseSeconds
                        ? `Winner cannot be changed in the last ${betCloseSeconds} seconds`
                        : !winnerX?.trim()
                          ? "Enter a multiplier (e.g. 2x)"
                          : jackpotXGridSlot < 1
                            ? "Select one winner yantra"
                            : undefined
                  }
                >
                  {applyJackpotXWinnerLoading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Applying...
                    </>
                  ) : (
                    "Apply"
                  )}
                </button>
              </div>
            </div>

            <div
              className="ls-admin-block ls-admin-manual-block"
              ref={manualPickerRef}
            >
              <div className="ls-admin-label">Manual</div>
              <div className="ls-admin-inline">
                <button
                  type="button"
                  className="ls-admin-picker-input"
                  onClick={() => setShowManualButtonsPicker((prev) => !prev)}
                >
                  {manualAllowedButtons
                    ? `Yantra: ${manualAllowedButtons}`
                    : "Select Allowed Yantra"}
                </button>
                <button
                  type="button"
                  className="ls-admin-apply-btn"
                  disabled={
                    !isCurrentGameLive ||
                    remainingSeconds <= betCloseSeconds ||
                    !manualAllowedButtons ||
                    applyManualWinnerLoading
                  }
                  onClick={applyManualWinner}
                  title={
                    !isCurrentGameLive
                      ? "Session must be Live to apply"
                      : remainingSeconds <= betCloseSeconds
                        ? `Winner cannot be changed in the last ${betCloseSeconds} seconds`
                        : undefined
                  }
                >
                  {applyManualWinnerLoading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Applying...
                    </>
                  ) : (
                    "Apply"
                  )}
                </button>
              </div>

              {showManualButtonsPicker && (
                <div className="ls-admin-popover">
                  <div className="ls-admin-popover-title">
                    SELECT ALLOWED YANTRA (ONE)
                  </div>
                  <div className="ls-admin-popover-grid">
                    {Array.from({ length: yantraLength }).map((_, idx) => {
                      const num = idx + 1;
                      const selected = manualAllowedButtons === num;
                      // The grid is positions, but which yantra a position
                      // holds differs per game - name it so the choice can
                      // be checked before it is applied.
                      const slotName =
                        liveActivity?.yantraWise?.[idx]?.yantra_name;
                      return (
                        <button
                          key={num}
                          type="button"
                          title={slotName || `Slot ${num}`}
                          className={`ls-admin-popover-button${
                            selected ? " selected" : ""
                          }`}
                          onClick={() => {
                            setManualAllowedButtons(num);
                          }}
                        >
                          {num}
                        </button>
                      );
                    })}
                  </div>
                  <AdminPopoverProfitPreview
                    preview={manualWinnerPickProfitPreview}
                    noDataHint="Live activity not loaded."
                    noSelectionHint="Select one position to preview payout and profit/loss (uses bet count on that yantra)."
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Super Admin/Admin view-only: show last 10s processing modal */}
      {isViewOnly &&
        Boolean(sessionId) &&
        remainingSeconds > 0 &&
        remainingSeconds <= betCloseSeconds && (
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Winner processing"
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0, 0, 0, 0.55)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
              pointerEvents: "all",
            }}
            onClick={(e) => e.preventDefault()}
          >
            <div
              style={{
                width: "min(360px, calc(100vw - 32px))",
                background: "#fff",
                borderRadius: 12,
                padding: "22px 20px",
                boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
                textAlign: "center",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ fontWeight: 700, fontSize: 18 }}>
                Winner processing
              </div>
              <div style={{ marginTop: 6, color: "#555" }}>
                Betting is closed in the last {betCloseSeconds} seconds.
              </div>
              <div style={{ marginTop: 10, color: "#111" }}>
                Next round starts soon ({remainingSeconds}s)
              </div>
              <div
                style={{
                  marginTop: 14,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 10,
                }}
              >
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="9"
                    fill="none"
                    stroke="#667eea"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeDasharray="42 56"
                  >
                    <animateTransform
                      attributeName="transform"
                      type="rotate"
                      from="0 12 12"
                      to="360 12 12"
                      dur="0.9s"
                      repeatCount="indefinite"
                    />
                  </circle>
                </svg>
                <span style={{ color: "#667eea", fontWeight: 600 }}>
                  Please wait…
                </span>
              </div>
            </div>
          </div>
        )}

      {isViewOnly ? (
        <div className="live-session-content">
          <div className="live-main-content">
            <div className="live-betting-panel">
              {liveActivityLoading && !liveActivity ? (
                <Loader message="Loading live activity..." />
              ) : liveActivity?.yantraWise?.length ? (
                <div
                  className="live-yantra-card-container"
                  style={getBoardGridStyle(
                    totalGhar || liveActivity.yantraWise.length,
                  )}
                >
                  {liveActivity.yantraWise.map((y) => {
                    const imgSrc = y.yantra_img
                      ? `${LIVE_YANTRA_IMAGE_URL}/${y.yantra_img}`
                      : `${LIVE_YANTRA_IMAGE_URL}/default_image.png`;
                    const isWinner = sessionWinnerIdSet.has(Number(y.yantra_id));
                    return (
                      <div
                        key={y.yantra_id}
                        className={`live-yantra-card live-yantra-card-view-only${
                          isWinner ? " live-yantra-card--winner" : ""
                        }`}
                      >
                        {isWinner && (
                          <span className="ls-winner-badge">
                            {/* "Multi Winner" already says winner; the rest read "Winner · SIMPAL". */}
                            {sessionWinnerTypeLabel === "Multi Winner"
                              ? "Multi Winner"
                              : `Winner${sessionWinnerTypeLabel ? ` · ${sessionWinnerTypeLabel}` : ""}`}
                          </span>
                        )}
                        <img
                          src={imgSrc}
                          alt={y.yantra_name}
                          className="live-yantra-image"
                        />
                        <h3 className="live-yantra-name">{y.yantra_name}</h3>
                        <div className="live-activity-view-only-count">
                          Count: {y.bet_count}
                        </div>
                        <div className="live-activity-view-only-amount">
                          ₹{Number(y.total_amount || 0).toFixed(2)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <NoData
                  message="No live activity"
                  subText="No running session or no bets yet"
                />
              )}
            </div>
          </div>
          <div className="live-activity-panel">
            <div className="live-activity-panel-title">
              <h3 className="live-activity-panel-title-text">Activity</h3>
              {sessionStatus === "RUNNING" && (
                <div className="live-activity-panel-title-live-status">
                  <GoDotFill /> <span>Live</span>
                </div>
              )}
            </div>
            {/* Label / value rows: half the height of the old one-per-line
                text and the numbers line up, so the whole screen fits
                without scrolling. */}
            <div className="ls-activity-stats">
              <div className="ls-activity-row">
                <span className="ls-activity-k">Bet</span>
                <span className="ls-activity-v">
                  {liveActivity?.totalBet ?? 0}
                </span>
              </div>
              <div className="ls-activity-row">
                <span className="ls-activity-k">Amount</span>
                <span className="ls-activity-v">
                  ₹{Number(liveActivity?.totalAmount ?? 0).toFixed(2)}
                </span>
              </div>
              <div className="ls-activity-row">
                <span className="ls-activity-k">Date</span>
                <span className="ls-activity-v">{initialValues?.date || "-"}</span>
              </div>
              <div className="ls-activity-row">
                <span className="ls-activity-k">Draw Time</span>
                <span className="ls-activity-v">
                  {initialValues?.draw_time || "-"}
                </span>
              </div>
              <div className="ls-activity-row">
                <span className="ls-activity-k">Current Time</span>
                <span className="ls-activity-v">
                  {currentTimeDisplay || initialValues?.current_time || "-"}
                </span>
              </div>
              <div className="ls-activity-row ls-activity-row--remaining">
                <span className="ls-activity-k">Remaining</span>
                <span className="ls-activity-v ls-activity-v--timer">
                  {formatRemaining(remainingSeconds)}
                </span>
              </div>
              <div className="ls-activity-row">
                <span className="ls-activity-k">Session</span>
                <span className="ls-activity-v">
                  #{initialValues?.session_no || "-"}
                </span>
              </div>
            </div>

            {/* Settlement figures for the round, same row style as above. */}
            <div className="ls-activity-stats ls-activity-stats--profit">
              <div className="ls-activity-row">
                <span className="ls-activity-k">Percentage %</span>
                <span className="ls-activity-v">
                  {Number(profitLossData?.percentage ?? 0)}%
                </span>
              </div>
              <div className="ls-activity-row">
                <span className="ls-activity-k">Amount(%)</span>
                <span className="ls-activity-v">
                  ₹{Number(profitLossData?.adminAmount ?? 0).toFixed(1)}
                </span>
              </div>
              <div className="ls-activity-row">
                <span className="ls-activity-k">Winning Payout</span>
                <span className="ls-activity-v">
                  ₹{Number(profitLossData?.winningAmount ?? 0).toFixed(2)}
                </span>
              </div>
              <div className="ls-activity-row">
                <span className="ls-activity-k">Profit/Loss</span>
                <span
                  className={`ls-activity-v ${
                    Number(profitLossData?.profitLoss ?? 0) >= 0 ? "pos" : "neg"
                  }`}
                >
                  ₹{Number(profitLossData?.profitLoss ?? 0).toFixed(1)}
                </span>
              </div>
              <div className="ls-activity-row ls-activity-row--net">
                <span className="ls-activity-k">Net Profit</span>
                <span
                  className={`ls-activity-v ${
                    Number(profitLossData?.netProfit ?? 0) >= 0 ? "pos" : "neg"
                  }`}
                >
                  ₹{Number(profitLossData?.netProfit ?? 0).toFixed(1)}
                </span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={async (
            values,
            { setSubmitting, resetForm, setFieldValue },
          ) => {
            setSubmitting(true);
            try {
              const response = await liveSSApi.savePlaceBetData(values);
              if (response.isSuccess) {
                toast.success("Bet placed successfully");

                // Prefer wallet balance coming from savePlaceBetData response (if backend returns it).
                // If not present, silently refresh wallet balance via getLiveSessionData.
                const newWalletBalanceRaw =
                  response?.walletData?.wallet_balance;
                const newWalletBalance =
                  newWalletBalanceRaw != null &&
                  !Number.isNaN(Number(newWalletBalanceRaw))
                    ? Number(newWalletBalanceRaw)
                    : null;

                const resetYantras = (values.yantra_list || []).map((y) => ({
                  ...y,
                  yantra_count: 0,
                }));

                resetForm({
                  values: {
                    ...values,
                    wallet_balance:
                      newWalletBalance != null
                        ? newWalletBalance
                        : values.wallet_balance,
                    total_bet: 0,
                    amount: 0,
                    yantra_list: resetYantras,
                  },
                });

                if (newWalletBalance == null) {
                  // Fallback: refresh wallet from backend running-game API (no UI reset / no toast).
                  try {
                    const refresh = await liveSSApi.getLiveSessionData(
                      activeTabRef.current,
                    );
                    const wb = refresh?.walletData?.wallet_balance;
                    const pa =
                      refresh?.walletData?.paid_amount ??
                      refresh?.walletData?.paid_amoun;

                    if (wb != null && !Number.isNaN(Number(wb))) {
                      setFieldValue("wallet_balance", Number(wb));
                    }
                    if (pa != null && !Number.isNaN(Number(pa))) {
                      setFieldValue("paid_amount", Number(pa));
                    }
                  } catch (_) {
                    // ignore refresh failure; bet already succeeded
                  }
                }
              } else {
                toast.error(response.message);
              }
            } catch (error) {
              console.error("Error in savePlaceBetData:", error);
              toast.dismiss();
              toast.error(
                error?.response?.status === 404
                  ? error?.response?.data?.message
                  : "Error save place bet data",
              );
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ values, errors, touched, isSubmitting, setFieldValue }) => {
            const list = values.yantra_list || [];
            const totalBet = list.reduce(
              (sum, y) => sum + (Number(y.yantra_count) || 0),
              0,
            );
            const amount = list.reduce(
              (sum, y) =>
                sum +
                (Number(y.yantra_count) || 0) * (Number(y.yantra_amount) || 0),
              0,
            );

            // Backend may mark session as COMPLETED slightly early in last 10s.
            // Keep modal visible for full 10s based on countdown only.
            const showWinnerProcessingModal =
              Boolean(sessionId) &&
              remainingSeconds > 0 &&
              remainingSeconds <= betCloseSeconds;

            const isPlaceBetDisabled =
              isSubmitting ||
              remainingSeconds <= betCloseSeconds ||
              sessionStatus !== "RUNNING" ||
              totalBet === 0;

            return (
              <Form className="live-session-form">
                <SyncTotalBetAmount />

                {showWinnerProcessingModal && (
                  <div
                    role="dialog"
                    aria-modal="true"
                    aria-label="Winner processing"
                    style={{
                      position: "fixed",
                      inset: 0,
                      background: "rgba(0, 0, 0, 0.55)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 9999,
                      pointerEvents: "all",
                    }}
                    onClick={(e) => e.preventDefault()}
                  >
                    <div
                      style={{
                        width: "min(360px, calc(100vw - 32px))",
                        background: "#fff",
                        borderRadius: 12,
                        padding: "22px 20px",
                        boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
                        textAlign: "center",
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div style={{ fontWeight: 700, fontSize: 18 }}>
                        Winner processing
                      </div>
                      <div style={{ marginTop: 6, color: "#555" }}>
                        Betting is closed in the last {betCloseSeconds} seconds.
                      </div>
                      <div style={{ marginTop: 10, color: "#111" }}>
                        Next round starts soon ({remainingSeconds}s)
                      </div>

                      <div
                        style={{
                          marginTop: 14,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 10,
                        }}
                      >
                        <svg
                          width="22"
                          height="22"
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                        >
                          <circle
                            cx="12"
                            cy="12"
                            r="9"
                            fill="none"
                            stroke="#667eea"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeDasharray="42 56"
                          >
                            <animateTransform
                              attributeName="transform"
                              type="rotate"
                              from="0 12 12"
                              to="360 12 12"
                              dur="0.9s"
                              repeatCount="indefinite"
                            />
                          </circle>
                        </svg>
                        <span style={{ color: "#333" }}>Please wait...</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="live-session-content">
                  <div className="live-main-content">
                    <div className="live-betting-panel">
                      {yantraLoading ? (
                        <Loader message="Loading Yantras..." />
                      ) : values.yantra_list.length === 0 ? (
                        <NoData
                          message="No Yantras Available"
                          subText="Please try again later"
                        />
                      ) : (
                        <FieldArray name="yantra_list">
                          {() => (
                            <div
                  className="live-yantra-card-container"
                  style={getBoardGridStyle(totalGhar || (values?.yantra_list || []).length)}
                >
                              {values.yantra_list.map((yantra, index) => (
                                <div
                                  key={yantra.yantra_id}
                                  className="live-yantra-card"
                                  onClick={() => {
                                    const current =
                                      Number(
                                        values.yantra_list[index].yantra_count,
                                      ) || 0;
                                    setFieldValue(
                                      `yantra_list.${index}.yantra_count`,
                                      current + 1,
                                    );
                                  }}
                                  role="button"
                                  tabIndex={0}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      const current =
                                        Number(
                                          values.yantra_list[index]
                                            .yantra_count,
                                        ) || 0;
                                      setFieldValue(
                                        `yantra_list.${index}.yantra_count`,
                                        current + 1,
                                      );
                                    }
                                  }}
                                >
                                  <img
                                    src={`${LIVE_YANTRA_IMAGE_URL}/${yantra.yantra_img}`}
                                    alt={yantra.yantra_name}
                                    className="live-yantra-image"
                                  />

                                  <h3 className="live-yantra-name">
                                    {yantra.yantra_name}
                                  </h3>

                                  <Field
                                    type="number"
                                    name={`yantra_list.${index}.yantra_count`}
                                    className="live-bet-input"
                                    min="0"
                                    onClick={(e) => e.stopPropagation()}
                                  />

                                  {errors.yantra_list?.[index]?.yantra_count &&
                                    touched.yantra_list?.[index]
                                      ?.yantra_count && (
                                      <div className="error">
                                        {errors.yantra_list[index].yantra_count}
                                      </div>
                                    )}
                                </div>
                              ))}
                            </div>
                          )}
                        </FieldArray>
                      )}
                    </div>
                  </div>

                  <div className="live-activity-panel">
                    <div className="live-activity-panel-title">
                      <h3 className="live-activity-panel-title-text">
                        Activity
                      </h3>
                      {sessionStatus === "RUNNING" && (
                        <div className="live-activity-panel-title-live-status">
                          <GoDotFill /> <span>Live</span>
                        </div>
                      )}
                    </div>
                    <div className="live-activity-item">
                      <div className="live-activity-item-title">
                        Wallet Balance
                      </div>
                      <div className="live-activity-item-value">
                        {values?.wallet_balance}
                      </div>
                    </div>
                    <div className="live-activity-item">
                      <div className="live-activity-item-title">Total Bet</div>
                      <div className="live-activity-item-value">{totalBet}</div>
                    </div>
                    <div className="live-activity-item">
                      <div className="live-activity-item-title">Amount</div>
                      <div className="live-activity-item-value">{amount}</div>
                    </div>
                    <div className="live-activity-item">
                      <div className="live-activity-item-title">Date</div>
                      <div className="live-activity-item-value">
                        {values?.date}
                      </div>
                    </div>
                    <div className="live-activity-item">
                      <div className="live-activity-item-title">Draw Time</div>
                      <div className="live-activity-item-value">
                        {values?.draw_time}
                      </div>
                    </div>
                    <div className="live-activity-item">
                      <div className="live-activity-item-title">
                        Remaining Time
                      </div>
                      <div className="live-activity-item-value">
                        {formatRemaining(remainingSeconds)}
                      </div>
                    </div>
                    <div className="live-activity-item">
                      <div className="live-activity-item-title">
                        Current Time
                      </div>
                      <div className="live-activity-item-value">
                        {currentTimeDisplay || values?.current_time}
                      </div>
                    </div>
                    <div className="live-activity-item">
                      <div className="live-activity-item-title">
                        Live Session
                        <span className="text-muted">
                          {" "}
                          #{values?.session_no}
                        </span>
                      </div>
                      <div className="live-activity-item-value">
                        #{sessionId}
                      </div>
                    </div>
                  </div>

                  <div>
                    <ErrorMessage name="wallet_balance">
                      {(msg) => <div className="live-error-text">{msg}</div>}
                    </ErrorMessage>
                    <ErrorMessage name="total_bet">
                      {(msg) => <div className="live-error-text">{msg}</div>}
                    </ErrorMessage>
                    <ErrorMessage name="yantra_list">
                      {(msg) =>
                        msg.map((error, index) => (
                          <div key={index} className="error">
                            {error}
                          </div>
                        ))
                      }
                    </ErrorMessage>

                    <button
                      type="button"
                      className="leave-btn"
                      onClick={() => setIsHistoryOpen(!isHistoryOpen)}
                    >
                      {isHistoryOpen ? "Close" : "History"}
                    </button>
                  </div>
                </div>
              </Form>
            );
          }}
        </Formik>
      )}

      {Number(profile?.role_id) === 1 && sessionDbId ? (
        <div className="ls-super-admin-purchase-section">
          <div className="ls-super-admin-purchase-head">
            <h3 className="ls-super-admin-purchase-title">
              Current session — purchase by user
            </h3>
          </div>
          <div className="ls-super-admin-purchase-scroll">
            <table className="ls-super-admin-purchase-table">
              <thead>
                <tr>
                  <th className="ls-sap-col-sr">Sr.</th>
                  <th className="ls-sap-col-name">Name</th>
                  {purchaseGridYantraIds.map((id, idx) => (
                    <th
                      key={id}
                      className="ls-sap-col-yantra"
                      title={purchaseGridYantraName(id)}
                    >
                      {idx + 1}
                    </th>
                  ))}
                  <th className="ls-sap-col-total">Total</th>
                </tr>
              </thead>
              <tbody>
                {(superAdminPurchaseGrid?.rows || []).length === 0 ? (
                  <tr>
                    <td
                      colSpan={
                        2 +
                        purchaseGridYantraIds.length +
                        1
                      }
                      className="ls-sap-empty"
                    >
                      No purchases in this session yet.
                    </td>
                  </tr>
                ) : (
                  (superAdminPurchaseGrid?.rows || []).map((row, idx) => {
                    const ids = purchaseGridYantraIds;
                    const total =
                      typeof row.total_units === "number"
                        ? row.total_units
                        : ids.reduce(
                            (s, id) =>
                              s +
                              (Number(row.yantra_counts?.[String(id)]) || 0),
                            0,
                          );
                    return (
                      <tr key={row.user_id}>
                        <td className="ls-sap-col-sr">{idx + 1}</td>
                        <td className="ls-sap-col-name">
                          <span className="ls-sap-name-text">
                            {row.user_name || "—"}
                          </span>
                          <span className="ls-sap-username">
                            {" "}
                            [{row.user_username || "—"}]
                          </span>
                        </td>
                        {ids.map((id) => {
                          const v =
                            Number(row.yantra_counts?.[String(id)]) || 0;
                          return (
                            <td key={id} className="ls-sap-col-yantra">
                              {v}
                            </td>
                          );
                        })}
                        <td className="ls-sap-col-total ls-sap-total-cell">
                          {total}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
      <div className="last-draw-wrapper">
        {historyLoading ? (
          <Loader message="Loading Last Draw..." />
        ) : history.length === 0 ? (
          <NoData
            message="No Last Draw Available"
            subText="Please try again later"
          />
        ) : (
          <div className="last-draw-section">
            <div className="d-flex justify-content-between align-items-center">
              <h3 className="last-draw-title">Last Draw</h3>
              <button
                type="button"
                className="last-all-draw-btn"
                onClick={() => routerHistory.push("/all-draw")}
              >
                All Draws
                <FiArrowRight size={18} />
              </button>
            </div>
            <div className="last-draw-grid">
              {history.flatMap((item) => {
                const endTime = item?.end_time
                  ? moment.utc(item.end_time).local()
                  : null;
                const dateLabel = endTime ? endTime.format("DD-MM-YYYY") : "";
                const timeLabel = endTime
                  ? endTime.format(TIME_DISPLAY_FORMAT)
                  : "";
                const winnerType = String(
                  item?.winner_type || "",
                ).toUpperCase();
                const isJackpot = winnerType === "JACKPOT" ? true : false;
                const jackpotBadgeLabel =
                  winnerType === "JACKPOT"
                    ? "Multi Winner"
                    : winnerType === "JACKPOTX"
                      ? (() => {
                          const raw = String(item?.winner_x || "").trim();
                          if (!raw) return "Jackpot";
                          if (/[xX]$/.test(raw)) return `Jackpot (${raw})`;
                          return `Jackpot (${raw}x)`;
                        })()
                      : null;
                const winners =
                  Array.isArray(item?.winner_yantras) &&
                  item.winner_yantras.length > 0
                    ? item.winner_yantras
                    : [
                        {
                          yantra_name: item?.yantra_name,
                          yantra_img: item?.yantra_img,
                          yantra_id: item?.winner_yantra,
                        },
                      ];

                if (isJackpot) {
                  const winnerNames = winners
                    .map(
                      (w) =>
                        w?.yantra_name ||
                        (w?.yantra_id ? `Yantra ${w.yantra_id}` : ""),
                    )
                    .filter(Boolean);
                  const namesLabel =
                    winnerNames.length > 0 ? winnerNames.join(" | ") : "-";
                  return [
                    <div
                      key={`${item.session_id}-jackpot`}
                      className="last-draw-card last-draw-card-jackpot"
                    >
                      <div className="last-draw-datetime">
                        <div>{dateLabel}</div>
                        <div>{timeLabel}</div>
                      </div>
                      <div className="last-draw-image-wrap">
                        <div className="last-draw-image-placeholder">
                          <span className="last-draw-image-placeholder-text">
                            Multi
                          </span>
                        </div>
                      </div>
                      {jackpotBadgeLabel && (
                        <div
                          className={`last-draw-winner-badge ${
                            winnerType === "JACKPOTX" ? "jackpotx" : "jackpot"
                          }`}
                          title={jackpotBadgeLabel}
                        >
                          {jackpotBadgeLabel}
                        </div>
                      )}
                      <div className="last-draw-name last-draw-name-list">
                        {namesLabel}
                      </div>
                      <div className="last-draw-winning-detail-position">
                        <button
                          type="button"
                          className="last-draw-winning-detail-btn"
                          title="View winning detail"
                          onClick={() => {
                            try {
                              window.sessionStorage.setItem(
                                "swd_session_id",
                                String(item.session_id),
                              );
                            } catch (_) {}
                            routerHistory.push("/session-winning-detail");
                          }}
                        >
                          <MdPreview size={18} />
                        </button>
                      </div>
                    </div>,
                  ];
                }

                return winners.map((w, idx) => {
                  const imgSrc = w?.yantra_img
                    ? `${LIVE_YANTRA_IMAGE_URL}/${w.yantra_img}`
                    : `${LIVE_YANTRA_IMAGE_URL}/default_image.png`;
                  return (
                    <div
                      key={`${item.session_id}-${w?.yantra_id ?? idx}`}
                      className={`last-draw-card ${winnerType === "JACKPOTX" ? "last-draw-card-jackpot" : ""}`}
                    >
                      {/* {jackpotBadgeLabel && (
                        <div
                          className={`last-draw-winner-badge ${
                            winnerType === "JACKPOTX" ? "jackpotx" : "jackpot"
                          }`}
                          title={jackpotBadgeLabel}
                        >
                          {jackpotBadgeLabel}
                        </div>
                      )} */}
                      <div className="last-draw-datetime">
                        <div>{dateLabel}</div>
                        <div>{timeLabel}</div>
                      </div>
                      <div className="last-draw-image-wrap">
                        {imgSrc ? (
                          <img src={imgSrc} alt={w?.yantra_name || "Yantra"} />
                        ) : (
                          <div className="last-draw-image-placeholder" />
                        )}
                      </div>

                      {jackpotBadgeLabel && (
                        <div
                          className={`last-draw-winner-badge ${
                            winnerType === "JACKPOTX" ? "jackpotx" : "jackpot"
                          }`}
                          title={jackpotBadgeLabel}
                        >
                          {jackpotBadgeLabel}
                        </div>
                      )}

                      <div className="last-draw-name">
                        {w?.yantra_name || `Yantra ${w?.yantra_id || ""}`}
                      </div>
                      <div className="last-draw-winning-detail-position">
                        <button
                          type="button"
                          className="last-draw-winning-detail-btn"
                          title="View winning detail"
                          onClick={() => {
                            try {
                              window.sessionStorage.setItem(
                                "swd_session_id",
                                String(item.session_id),
                              );
                            } catch (_) {}
                            routerHistory.push("/session-winning-detail");
                          }}
                        >
                          <MdPreview size={18} />
                        </button>
                      </div>
                    </div>
                  );
                });
              })}
            </div>
          </div>
        )}
      </div>

      {profile?.role_id === 1 && lsOnlineModalMeta && (
        <div
          className="ls-online-modal-backdrop"
          role="presentation"
          onClick={() => setLsOnlineModalRole(null)}
        >
          <div
            className="ls-online-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ls-online-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ls-online-modal-head">
              <h3 id="ls-online-modal-title">{lsOnlineModalMeta.title}</h3>
              <button
                type="button"
                className="ls-online-modal-close"
                onClick={() => setLsOnlineModalRole(null)}
                aria-label="Close"
              >
                <MdClose size={24} />
              </button>
            </div>
            <ul className="ls-online-modal-list">
              {lsOnlineModalList.length === 0 ? (
                <li className="ls-online-modal-empty">
                  No users online in this role.
                </li>
              ) : (
                lsOnlineModalList.map((u) => (
                  <li key={u.user_id}>{formatLsOnlineUserLine(u)}</li>
                ))
              )}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveSession;
